import DraggableModal from '@/common/ant-components/DraggableModal';
import RadioButtonGroup from '@/common/ant-components/RadioGroupReport';
import { NewNepaliDatePicker } from '@/common/customNepaliDatePicker';
import LedgerForm from '@/components/forms/master/account/ledger/LedgerForm';
import {
  VoucherFooterButton,
  VoucherTable,
} from '@/pages/vouchers/VoucherComponent';
import {
  getAdFromBS,
  getBsFromAD,
  getTodayDate,
} from '@/utils/helpers/getTodayDate';
import GlobalFormModal from '@/utils/modal/GlobalFormModal';
import CustomSelect from '@/utils/search/Search';
import { DeleteFilled, PlusCircleFilled } from '@ant-design/icons';
import {
  Col,
  ConfigProvider,
  DatePicker,
  Form,
  Input,
  Row,
  Select,
  Table,
  theme,
} from 'antd';
import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { dateDisabled } from '../reuse/helpers';
import { ShowAllData } from '@/apiError/apiShow';
import { dateType as backendDateType } from '@/common/custom-date-selector-list/helpers';
import useAppData from '@/hooks/useAppData';

const CashPaidModal = ({
  visible,
  onClose,
  selectedData,
  billNumber,
  dataSource,
  setDataSource,
  cashBankLedgerData,
  onTotalUpdate,
  refreshTable,
  id,
  isEditMode,
}) => {
  const [count, setCount] = useState(1);
  const [total, setTotal] = useState(0);
  const [selectedItems, setSelectedItems] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const app = useAppData();

  const [dateType, setDateType] = useState(app?.app?.dateType);
  const [form] = Form.useForm();

  const fieldName = 'payFrom';
  const rowTemplate = { payFrom: '', amount: 0, remarks: '' };

  const handleSelectChange = (value, record) => {
    // Update the data source with the selected value for the row
    const updatedDataSource = dataSource.map((item) =>
      item.key === record.key ? { ...item, payFrom: value } : item,
    );
    setDataSource(updatedDataSource);

    // Update selectedItems with the selected value for the row
    setSelectedItems((prev) => [...prev, value]);
    handleSave(updatedDataSource.find((item) => item.key === record.key)); // Update with new value
  };

  const handleRadio = (e) => {
    setDateType(e?.target?.value);
  };

  const handleAdd = (rowTemplate) => {
    const newData = { key: count, ...rowTemplate };
    setDataSource((prevData) => {
      const newDataSource = [...prevData, newData];
      setSelectedItems(
        newDataSource?.map((item) => item[fieldName]).filter(Boolean),
      );
      return newDataSource;
    });
    setCount((prev) => prev + 1);
  };

  const handleSave = (row) => {
    const newData = dataSource?.map((item) =>
      item.key === row.key ? { ...item, ...row } : item,
    );
    setDataSource(newData);
    setSelectedItems(newData.map((item) => item[fieldName]).filter(Boolean)); // Update selectedItems
    recalculateTotal(newData);
  };

  // Delete row
  const handleDelete = (key) => {
    if (dataSource.length === 1) {
      // If there's only one row, clear its data (don't delete it)
      const resetData = [
        { key: dataSource[0].key, fieldName: '', amount: 0, remarks: '' },
      ];
      setDataSource(resetData);
      setSelectedItems([]); // Reset selected items since the row is now empty
      recalculateTotal(resetData);
    } else {
      // If there's more than one row, delete the selected row
      const newData = dataSource.filter((item) => item.key !== key);
      setDataSource(newData);
      setSelectedItems(newData.map((item) => item.payFrom).filter(Boolean)); // Update selectedItems
      recalculateTotal(newData);
    }
  };

  // Recalculate the total amount
  const recalculateTotal = (data) => {
    const total = data.reduce((acc, item) => acc + parseFloat(item.amount), 0);
    setTotal(Number(total));
    if (onTotalUpdate) {
      onTotalUpdate(total);
    }
  };

  const navigate = useNavigate();

  useEffect(() => {
    const loadData = async () => {
      if (!id) {
        const response = await ShowAllData.showDetails(
          'account/payment-vouchers/latest-number/',
          navigate,
        );
        form.setFieldsValue(response.data);
      }
    };
    loadData();
  }, []);

  // const recalculateTotal = useCallback(
  //   (data) => {
  //     const total = data.reduce(
  //       (acc, item) => acc + parseFloat(item.amount),
  //       0,
  //     );
  //     setTotal(Number(total));
  //     onTotalUpdate?.(total);
  //   },
  //   [dataSource],
  // );

  useEffect(() => {
    if (dataSource) {
      // const mappedItems = dataSource
      //   ?.map((item) => item?.payFromId)
      //   .filter(Boolean);
      // setSelectedItems(mappedItems);s
      recalculateTotal(dataSource);
    }
  }, [dataSource]);

  useEffect(() => {
    if (selectedData?.entryDate) {
      const currentDate =
        typeof selectedData.entryDate === 'string'
          ? dayjs(selectedData.entryDate)
          : selectedData.entryDate;

      form.setFieldsValue({
        entryDate: currentDate,
        entry_date_bs: getBsFromAD(currentDate),
      });
    }
  }, [selectedData]);

  const VoucherData = dataSource?.map((data, index) => ({
    sno: index + 1,
    ...data,
  }));

  const handleAddClick = () => {
    setIsModalVisible(true);
  };

  const closeModal = () => {
    setIsModalVisible(false);
  };

  const columns = [
    {
      title: 'S.No',
      dataIndex: 'sno',
      width: '10%',
      align: 'center',
    },
    {
      title: 'Particular',
      dataIndex: 'payFrom',
      align: 'center',
      width: '40%',
      render: (_, record) => (
        <CustomSelect
          placeholder="Select Ledger"
          style={{ width: '100%' }}
          value={record.payFrom}
          onChange={(value) => handleSelectChange(value, record)}
          options={
            cashBankLedgerData
              ? cashBankLedgerData?.particularLedger?.map((ledger) => ({
                  key: ledger.referenceId,
                  value: ledger.referenceId,
                  label: ledger.name,
                  disabled:
                    selectedItems.includes(ledger.referenceId) &&
                    record.payFrom !== ledger.referenceId, // Disable if already selected in another row
                }))
              : []
          }
          onAddClick={handleAddClick}
        />
      ),
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      editable: true,
      align: 'center',
      render: (_, record) => (
        <Input
          defaultValue={0}
          className="tw-text-right"
          value={record?.amount}
          onChange={(e) => {
            const value = e.target.value;
            if (/^\d*\.?\d*$/.test(value)) {
              // Allow only numbers and a single decimal point
              handleSave({
                ...record,
                amount: value, // Save as a string to handle intermediate states like "3."
              });
            }
          }}
        />
      ),
    },
    {
      title: 'Remark',
      dataIndex: 'remarks',
      editable: true,
      align: 'center',
      render: (_, record) => (
        <Input
          value={record.remarks}
          onChange={(e) => handleSave({ ...record, remarks: e.target.value })}
        />
      ),
    },
    {
      title: 'Action',
      dataIndex: 'action',
      align: 'center',
      render: (_, record) => (
        <section className="flex gap-1 justify-center">
          <Link>
            <PlusCircleFilled onClick={() => handleAdd(rowTemplate)} />
          </Link>
          <Link className="delete" style={{ color: 'red' }}>
            <DeleteFilled onClick={() => handleDelete(record.key)} />
          </Link>
        </section>
      ),
    },
  ];
  const tableSummary = (
    <>
      <Table.Summary.Row style={{ lineHeight: 'inherit' }}>
        <Table.Summary.Cell>Total</Table.Summary.Cell>
        <Table.Summary.Cell />
        <Table.Summary.Cell className="tw-text-right">
          {total || 0}
        </Table.Summary.Cell>
        <Table.Summary.Cell />
      </Table.Summary.Row>
    </>
  );

  return (
    <>
      <DraggableModal
        open={visible}
        setOpen={() => {}}
        onCancel={onClose}
        width={1000}
        title={
          <div className="tw-text-center tw-font-[600] tw-text-base">
            Payment Voucher
          </div>
        }
        footer={
          <VoucherFooterButton id={id} isFromModal={true} onClose={onClose} />
        }
      >
        <Form form={form}>
          <Row
            style={{
              paddingLeft: '15px',
              paddingRight: '15px',
              paddingTop: '10px',
            }}
            gutter={[16, 16]}
            justify="space-between"
          >
            <Col xs={24} md={12} lg={4}>
              <Form.Item
                label="Voucher no:"
                name={'voucherNumber'}
                style={{ fontWeight: 500 }}
              >
                <Input disabled value={billNumber?.voucherNumber} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12} lg={8}>
              <Form.Item label="Paid To:" style={{ fontWeight: 500 }}>
                <Select
                  value={
                    cashBankLedgerData?.paidToLedger?.some(
                      (item) => item.referenceId === selectedData?.ledger,
                    )
                      ? selectedData?.ledger
                      : null // Show empty if no match
                  }
                  style={{ width: '100%' }}
                  disabled
                >
                  {cashBankLedgerData?.paidToLedger?.map((ledger) => (
                    <Select.Option
                      key={ledger.referenceId}
                      value={ledger.referenceId}
                      disabled
                    >
                      {ledger.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            {/* <Form.Item
            label="Date:"
            layout="vertical"
            style={{
              width: 100,
            }}
          >
            <DatePicker value={selectedData.entryDate} />
          </Form.Item> */}
            <Col
              xs={24}
              md={12}
              lg={3}
              style={{
                marginLeft: 'auto',
              }}
            >
              <RadioButtonGroup
                name="date_type"
                initialValue={dateType}
                onChange={handleRadio}
              />
            </Col>
            <Col
              xs={dateType === 'bs' ? 0 : 24}
              md={dateType === 'bs' ? 0 : 12}
              lg={dateType === 'bs' ? 0 : 7}
            >
              <Form.Item
                name="entryDate"
                label="Entry Date(AD):"
                className="username"
                rules={[{ required: true, message: '' }]}
                hidden={dateType === 'bs'}
              >
                <DatePicker
                  style={{ width: '100%' }}
                  className="date-picker "
                  format="YYYY-MM-DD"
                  onChange={(date) => {
                    if (date) {
                      form.setFieldsValue({
                        entry_date_bs: getBsFromAD(date),
                      });
                    }
                  }}
                  disabledDate={(current) => dateDisabled(current, 'after')}
                />
              </Form.Item>
            </Col>
            <Col
              xs={dateType === 'ad' ? 0 : 24}
              md={dateType === 'ad' ? 0 : 12}
              lg={dateType === 'ad' ? 0 : 7}
            >
              <NewNepaliDatePicker
                formProps={{
                  name: 'entry_date_bs',
                  label: 'Entry Date (BS)',
                  initialValue: getTodayDate(),
                  hidden: dateType === 'ad',
                  rules: [
                    {
                      required: true,
                      message: '',
                    },
                  ],
                }}
                disabledAfter={getTodayDate()}
                // ref={nepaliDateRef}
                onChange={(e) => {
                  if (!e) return;
                  if (e > getTodayDate()) {
                    form.setFieldsValue({ entry_date_bs: getTodayDate() });
                    return;
                  }
                  const adDate = getAdFromBS(e);
                  form.setFieldsValue({ entryDate: adDate });
                }}
              />
            </Col>
          </Row>
        </Form>

        <div style={{ marginTop: '20px' }}>
          <VoucherTable
            columns={columns}
            dataSource={VoucherData}
            summary={tableSummary}
          />
        </div>
        <GlobalFormModal isVisible={isModalVisible} onClose={closeModal}>
          <LedgerForm
            onClose={closeModal}
            refreshTable={refreshTable}
            showCashBankAccount={true}
          />
        </GlobalFormModal>
      </DraggableModal>
      {/* <Modal
        open={visible}
        onCancel={onClose}
        title={<div className="tw-flex tw-justify-center">Payment Voucher</div>}
        footer={
          <VoucherFooterButton id={id} isFromModal={true} onClose={onClose} />
        }
        width={900}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            // marginBottom: '20px',
          }}
        >
          <p className="username">
            Voucher no: <Input disabled value={billNumber?.voucherNumber} />
          </p>
          <p className="username">
            Date: <DatePicker value={selectedData.entryDate} />
          </p>
        </div>
        <p className="username">Paid To:</p>
        <Select
          value={
            cashBankLedgerData?.paidToLedger?.some(
              (item) => item.referenceId === selectedData?.ledger,
            )
              ? selectedData?.ledger
              : null // Show empty if no match
          }
          style={{ width: '30%' }}
          disabled
        >
          {cashBankLedgerData?.paidToLedger?.map((ledger) => (
            <Select.Option
              key={ledger.referenceId}
              value={ledger.referenceId}
              disabled
            >
              {ledger.name}
            </Select.Option>
          ))}
        </Select>
        <div style={{ marginTop: '20px' }}>
          <VoucherTable
            columns={columns}
            dataSource={VoucherData}
            summary={tableSummary}
          />
        </div>
        <GlobalFormModal isVisible={isModalVisible} onClose={closeModal}>
          <LedgerForm
            onClose={closeModal}
            refreshTable={refreshTable}
            showCashBankAccount={true}
          />
        </GlobalFormModal>
      </Modal> */}
    </>
  );
};

export default CashPaidModal;
